import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Stack,
  Box,
  Divider,
  Avatar,
  IconButton,
  Paper,
  Slide,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import BusinessIcon from "@mui/icons-material/Business";
import {
  UpdateCompanyAdmin,
  GetCompanyAdmin,
} from "../../../services/jobService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { useDispatch } from "react-redux";
import EditDetailsDialog from "./EditDetailsDialog";
// Transition effect
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AdminDetailsDialog = ({ open, onClose, admin }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [editOpen, setEditOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);

  const [adminDetails, setAdminDetails] = useState({
    company_name: "",
    admin_email: "",
    phone: "",
  });

  const [formData, setFormData] = useState({
    name: "",
    admin_email: "",
    phone: "",
  });

  // Fetch admin details
  const fetchAdminDetails = async () => {
    try {
      setFetchLoading(true);
      const response = await GetCompanyAdmin();

      if (!response.error && response.company) {
        const companyData = {
          company_name: response.company.name,
          admin_email: response.company.admin_email,
          phone: response.company.phone,
        };

        setAdminDetails(companyData);

        setFormData({
          name: response.company.name,
          admin_email: response.company.admin_email,
          phone: response.company.phone,
        });
      } else {
        console.error("Error in admin details response:", response);
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: "Failed to fetch admin details.",
          })
        );
      }
      setFetchLoading(false);
    } catch (error) {
      console.error("Error fetching admin details:", error);
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: "Failed to fetch admin details. Please try again.",
        })
      );
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAdminDetails();
    }
  }, [open]);

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async (updatedData) => {
    try {
      setLoading(true);

      //  payload
      // const payload = {
      //   name: formData.name,
      //   admin_email: formData.admin_email,
      //   phone: formData.phone,
      // };

      const response = await UpdateCompanyAdmin(updatedData);

      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage:
            response.message || "Company admin details updated successfully",
        })
      );

      setEditOpen(false);

      await fetchAdminDetails();

      setLoading(false);
    } catch (error) {
      console.error("Error updating admin details:", error);

      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage:
            error.message ||
            "Failed to update admin details. Please try again.",
        })
      );
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  if (fetchLoading && open) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: theme.palette.primary.main,
            color: "white",
            p: 2,
          }}
        >
          <Typography variant="h6" fontWeight="500">
            Admin Details
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading admin details...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: theme.palette.primary.main,
            color: "white",
            p: 2,
          }}
        >
          <Typography variant="h6" fontWeight="500">
            Admin Details
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
              mt: 1,
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: theme.palette.primary.main,
                fontSize: 24,
                mb: 2,
              }}
            >
              {getInitials(admin?.name)}
            </Avatar>
            <Typography variant="h5" fontWeight="500">
              {admin?.name}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ py: 1 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                display: "flex",
                alignItems: "center",
                bgcolor: theme.palette.background.default,
                borderRadius: 2,
              }}
            >
              <BusinessIcon
                sx={{ mr: 2, color: theme.palette.text.secondary }}
              />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Company name
                </Typography>
                <Typography variant="body1">
                  {adminDetails.company_name}
                </Typography>
              </Box>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                display: "flex",
                alignItems: "center",
                bgcolor: theme.palette.background.default,
                borderRadius: 2,
              }}
            >
              <EmailIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {adminDetails.admin_email}
                </Typography>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                bgcolor: theme.palette.background.default,
                borderRadius: 2,
              }}
            >
              <PhoneIcon sx={{ mr: 2, color: theme.palette.text.secondary }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1">{adminDetails.phone}</Typography>
              </Box>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={onClose}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              mr: 1,
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => setEditOpen(true)}
            variant="contained"
            startIcon={<EditIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            Edit Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <EditDetailsDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        formData={formData}
        setFormData={setFormData}
        handleSave={handleSave}
        loading={loading}
      />
    </>
  );
};

export default AdminDetailsDialog;
